const PAD_COUNT = 64;
const MAX_CHROMATIC_FALLBACK_SEMITONES = 36;
export const SPARSE_CHROMATIC_OCTAVE_CORRECTION_CENTS = -1200;

export function sparseChromaticTuningCents(sourceNote, note) {
  return (note - sourceNote) * 100 + SPARSE_CHROMATIC_OCTAVE_CORRECTION_CENTS;
}

function hasSample(pad) {
  return (pad?.samples?.length ?? 0) > 0;
}

function isMostlyChromaticPadMap(padToNote) {
  if (!padToNote || padToNote.length !== PAD_COUNT) return false;
  let adjacentSteps = 0;
  let comparableSteps = 0;
  for (let i = 1; i < PAD_COUNT; i++) {
    const prev = padToNote[i - 1];
    const next = padToNote[i];
    if (!Number.isFinite(prev) || !Number.isFinite(next)) continue;
    comparableSteps++;
    if (next - prev === 1) adjacentSteps++;
  }
  return comparableSteps > 0 && adjacentSteps / comparableSteps >= 0.75;
}

function isChromaticFallbackEligible({ pads, padToNote, profile }) {
  if (profile?.os !== 'jjos') return false;
  if (!isMostlyChromaticPadMap(padToNote)) return false;
  const populatedPads = pads.filter(hasSample);
  return populatedPads.length > 0 && populatedPads.length < PAD_COUNT;
}

function nearestChromaticSourcePad(pads, padToNote, note) {
  let best = null;
  for (const pad of pads) {
    if (!hasSample(pad) || pad.chopTotalSlices > 1) continue;
    const sourceNote = padToNote[pad.index];
    if (!Number.isFinite(sourceNote)) continue;
    const distance = Math.abs(note - sourceNote);
    if (distance > MAX_CHROMATIC_FALLBACK_SEMITONES) continue;
    if (
      !best ||
      distance < best.distance ||
      (distance === best.distance && sourceNote <= note && best.sourceNote > note) ||
      (distance === best.distance && sourceNote === best.sourceNote && pad.index < best.pad.index)
    ) {
      best = { pad, sourceNote, distance };
    }
  }
  return best;
}

function clonePadWithChromaticTuning(source, { note, sourceNote, targetPadIndex }) {
  const rawCents = (note - sourceNote) * 100;
  // JJOS sparse chromatic programs behave like MPC 16 Levels tuning: the
  // first chromatic pad is one octave below the source pad's natural pitch.
  const cents = sparseChromaticTuningCents(sourceNote, note);
  return {
    ...source,
    index: source.index,
    resolvedFromPadIndex: targetPadIndex,
    resolvedFromNote: note,
    resolvedBy: 'chromatic-fallback',
    chromaticSourceNote: sourceNote,
    chromaticTuningCents: cents,
    chromaticRawTuningCents: rawCents,
    chromaticOctaveCorrectionCents: SPARSE_CHROMATIC_OCTAVE_CORRECTION_CENTS,
    samples: source.samples.map((sample) => ({
      ...sample,
      tuning: (sample.tuning ?? 0) + cents,
      chromaticTuningCents: cents,
      chromaticRawTuningCents: rawCents,
      chromaticOctaveCorrectionCents: SPARSE_CHROMATIC_OCTAVE_CORRECTION_CENTS,
    })),
  };
}

/**
 * Attach the common program note resolver used by .PGM and .PAC programs.
 * Exact note->pad mapping always wins. The fallback is intentionally narrow:
 * sparse JJOS chromatic programs can record pitched notes that map to empty
 * pads, with the populated sample pad acting as the source to retune.
 */
export function attachProgramNoteResolver(program) {
  const { pads, padToNote, noteToPad, profile } = program;
  const chromaticFallbackEligible = isChromaticFallbackEligible({ pads, padToNote, profile });

  program.resolveNote = function resolveNote(note) {
    const padIdx = noteToPad?.[note];
    if (padIdx == null || padIdx >= PAD_COUNT) return { pad: null, fallback: null };

    const exactPad = pads[padIdx];
    if (hasSample(exactPad) || !chromaticFallbackEligible) {
      return { pad: exactPad ?? null, fallback: null };
    }

    const source = nearestChromaticSourcePad(pads, padToNote, note);
    if (!source) return { pad: exactPad ?? null, fallback: null };

    const pad = clonePadWithChromaticTuning(source.pad, {
      note,
      sourceNote: source.sourceNote,
      targetPadIndex: padIdx,
    });
    return {
      pad,
      fallback: {
        type: 'chromatic',
        sourcePadIndex: source.pad.index,
        sourceNote: source.sourceNote,
        targetPadIndex: padIdx,
        note,
        tuningCents: pad.chromaticTuningCents,
        rawTuningCents: pad.chromaticRawTuningCents,
        octaveCorrectionCents: pad.chromaticOctaveCorrectionCents,
      },
    };
  };

  program.padForNote = function padForNote(note) {
    return program.resolveNote(note).pad;
  };

  return program;
}
